/**
 * The Info tab (fix D / R3). Rows of label + value over hairlines — no cards,
 * no chips-in-a-band (R6.1).
 *
 * The keyboard hint lives here, which is where the "← → to browse" line went
 * when the MoveList lost its header (R6.2): it is a thing you look up once, not
 * a permanent line above the notation.
 */
import React from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';

import {SIZE, SPACE} from '../../../theme/tokens';
import {Avatar, Button, Divider, Txt} from '../../../ui';
import ParentalRulesSection from '../ParentalRules';
import {InfoPanelModel, InfoSeat} from './model';

const Row = React.memo(function Row({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <View style={styles.row}>
      <Txt variant="caption" tone="muted">
        {label}
      </Txt>
      <Txt variant="body" tone="ink" numeric style={styles.value}>
        {value}
      </Txt>
    </View>
  );
});

const Seat = React.memo(function Seat({seat}: {seat: InfoSeat}) {
  return (
    <View style={styles.seat}>
      <Avatar name={seat.name} size="sm" tone={seat.isMe ? 'ev' : 'alt'} />
      <View style={styles.seatText}>
        <Txt variant="rowName" numberOfLines={1}>
          {seat.name}
        </Txt>
        <Txt variant="captionTight" tone="muted" numeric numberOfLines={1}>
          {seat.rating ? `${seat.rating} · ${seat.color}` : seat.color}
        </Txt>
      </View>
    </View>
  );
});

export default React.memo(function InfoPanelView({
  seats,
  chips,
  mode,
  customStart,
  result,
  gameUuid,
  canReview,
  onReview,
  rules,
  assistedBanner,
}: InfoPanelModel) {
  return (
    <ScrollView
      testID="v2-info-panel"
      style={styles.panel}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.content}>
      {rules.length > 0 && <ParentalRulesSection rules={rules} />}

      {!!assistedBanner && (
        <View testID="v2-assisted-banner" style={styles.banner}>
          <Txt variant="caption" tone="ev">
            {assistedBanner}
          </Txt>
        </View>
      )}

      {seats.map(seat => (
        <Seat key={seat.color} seat={seat} />
      ))}

      <Divider />

      {chips.length > 0 && <Row label="Game" value={chips.join(' · ')} />}
      <Row label="Mode" value={mode} />
      {customStart && (
        <Row label="Start position" value="Custom (FEN from the lobby)" />
      )}
      {!!result && <Row label="Result" value={result} />}
      {!!gameUuid && <Row label="Game id" value={gameUuid} />}

      <Divider />

      <Txt variant="caption" tone="faint">
        ← → steps through the moves · Home and End jump to the ends
      </Txt>

      {canReview && (
        <View style={styles.review}>
          <Button
            label="Open full review"
            variant="outline"
            size="md"
            icon="chart"
            onPress={onReview}
          />
        </View>
      )}
    </ScrollView>
  );
});

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    minHeight: 0,
  },
  content: {
    gap: SPACE.md,
    paddingBottom: SPACE.md,
  },
  seat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.md,
    height: SIZE.avatarLg,
  },
  seatText: {
    flex: 1,
    minWidth: 0,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: SPACE.md,
  },
  value: {
    flexShrink: 1,
    textAlign: 'right',
  },
  review: {
    paddingTop: SPACE.md,
  },
  // fix F-D / D2: a quiet strip, not a card — this panel draws rows, not cards
  banner: {
    paddingVertical: SPACE.xs,
  },
});
